/**
 * Who put the money in: one person covering it, or several splitting the
 * payment between them.
 *
 * The other half of the sheet from `SplitEditor` — that one is who *owes*,
 * this one is who *paid* — and, like it, it draws a draft and hands changes
 * back while everything that decides anything stays in
 * `src/lib/split-draft.ts`. `payersFor` is what says whether the amounts
 * typed here add up to the bill, and it is the same function the sheet's own
 * `resolveSheet` runs, so the sentence beside the toggle and the reason the
 * Save button is disabled cannot disagree.
 *
 * Shared by the expense sheet and the recurring one. The only thing that
 * differs between them is tense: an expense is money that has moved, a
 * recurring rule is money that will, and "Ben paid" on a rule that has never
 * fired would be the sheet asserting something that has not happened.
 */

import type { Member } from '../api'
import { formatMoney, parseMoneyInput } from '../lib/format'
import { payersFor, type PayerDraft } from '../lib/split-draft'

/** Money that has moved, or money that is going to. */
export type Tense = 'past' | 'present'

const WORDS: Record<Tense, { legend: string; toSeveral: string; toSole: string; putIn: string }> = {
  past: {
    legend: 'Who paid?',
    toSeveral: 'Several people paid',
    toSole: 'Just one person paid',
    putIn: 'put in',
  },
  present: {
    legend: 'Who pays?',
    toSeveral: 'Several people pay',
    toSole: 'Just one person pays',
    putIn: 'puts in',
  },
}

export function PayerEditor({
  payers,
  amountCents,
  currency,
  myId,
  members,
  tense = 'past',
  onChange,
}: {
  payers: PayerDraft
  amountCents: number
  currency: string
  myId: string
  /** In the group's own order — the order an odd cent is broken by. */
  members: readonly Member[]
  tense?: Tense
  onChange: (next: PayerDraft) => void
}) {
  const words = WORDS[tense]
  const memberOrder = members.map((member) => member.userId)
  const reading = payersFor(payers, amountCents, memberOrder)

  const nameOf = (member: Member) =>
    member.userId === myId ? `${member.displayName} (you)` : member.displayName

  return (
    <fieldset className="form__row">
      <legend>{words.legend}</legend>

      {!payers.several ? (
        <select
          aria-label={words.legend.replace('?', '')}
          value={payers.soleUserId}
          onChange={(event) => onChange({ ...payers, soleUserId: event.target.value })}
        >
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {nameOf(member)}
            </option>
          ))}
        </select>
      ) : (
        <ul className="split-editor__rows">
          {members.map((member) => (
            <li key={member.userId} className="split-row">
              <span className="split-row__who">{nameOf(member)}</span>
              <span className="split-row__input">
                <input
                  inputMode="decimal"
                  aria-label={`${member.displayName} ${words.putIn}`}
                  placeholder="0.00"
                  value={payers.amounts[member.userId] ?? ''}
                  onChange={(event) =>
                    onChange({
                      ...payers,
                      amounts: { ...payers.amounts, [member.userId]: event.target.value },
                    })
                  }
                />
              </span>
              {/* Echoed back as money, so a typo is visible as one. */}
              <span className="split-row__owes">
                {putIn(payers.amounts[member.userId], currency)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="actions actions--tight">
        <button
          type="button"
          className="button--quiet"
          onClick={() => onChange({ ...payers, several: !payers.several })}
        >
          {payers.several ? words.toSole : words.toSeveral}
        </button>
        {/*
          Only once there is an amount to reconcile against. "The payers put in
          0.00, which is not the 0.00 being split" is a complaint about a form
          nobody has finished filling in yet.
        */}
        {payers.several && reading.error !== null && amountCents > 0 && (
          <span className="bad note">{reading.error}</span>
        )}
      </p>
    </fieldset>
  )
}

/** One payer's contribution, as money. */
function putIn(typed: string | undefined, currency: string): string {
  const trimmed = (typed ?? '').trim()
  if (trimmed === '') return '—'
  const cents = parseMoneyInput(trimmed)
  return cents === null ? '?' : formatMoney(cents, currency)
}
